import * as React from 'react';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useMst } from '../../../../internal';
import styles from './QuizBlacklistSection.module.scss';

const BLACKLIST_PAGE_SIZE = 10;
const FLAGGED_STATUS_FILTERS = ['all', 'potential_fraud', 'not_fraud'] as const;

type FlaggedStatusFilter = typeof FLAGGED_STATUS_FILTERS[number];

type FlaggedUser = Record<string, any>;
type BlacklistEntry = Record<string, any>;
type QuizItem = Record<string, any>;
type QuizAnswer = Record<string, any>;

const getValue = (obj: Record<string, any>, keys: string[]) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== '') {
      return obj[key];
    }
  }

  return undefined;
};

const getFlaggedUserIdentifier = (user: FlaggedUser): string => {
  return String(
    getValue(user, [
      'identifier',
      'uid',
      'email',
      'user_email',
      'phone',
      'mobile',
      'msisdn',
    ]) || '-'
  );
};

const getFlaggedUserEmail = (user: FlaggedUser): string => {
  const email = getValue(user, ['email', 'user_email']);

  if (email) {
    return String(email);
  }

  return getFlaggedUserIdentifier(user);
};

const getFlaggedUserKey = (user: FlaggedUser, index: number): string => {
  return String(
    getValue(user, [
      'id',
      'userQuizId',
      'user_id',
      'account_id',
      'identifier',
      'email',
    ]) || `${getFlaggedUserIdentifier(user)}-${index}`
  );
};

const getFlaggedUserCompletedAt = (user: FlaggedUser): string | undefined => {
  const value = getValue(user, [
    'quiz_completed_at',
    'completed_at',
    'finished_at',
    'quiz_finished_at',
    'updatedAt',
    'createdAt',
    'created_at',
  ]);

  return value ? String(value) : undefined;
};

const getFlaggedUserReason = (user: FlaggedUser): string => {
  return String(
    getValue(user, ['reason', 'description', 'cheating_reason', 'note']) || '-'
  );
};

const getFlaggedUserStatus = (user: FlaggedUser): string => {
  return String(getValue(user, ['status']) || '-');
};

const getFraudEntryActionId = (
  entry: FlaggedUser | BlacklistEntry
): string | undefined => {
  const value = getValue(entry, ['id', 'userQuizId', 'user_id', 'userId']);
  return value !== undefined && value !== null ? String(value) : undefined;
};

const getFraudEntryAnswers = (
  entry: FlaggedUser | BlacklistEntry
): QuizAnswer[] => {
  return Array.isArray(entry?.answers) ? entry.answers : [];
};

const getAnswerQuestionText = (answer: QuizAnswer): string => {
  return String(
    getValue(answer, ['questionText', 'question', 'question_text']) || '-'
  );
};

const getAnswerText = (answer: QuizAnswer): string => {
  return String(
    getValue(answer, ['answerText', 'answer', 'answer_text']) || '-'
  );
};

const getAnswerDuration = (answer: QuizAnswer): number | undefined => {
  const value = getValue(answer, ['duration']);

  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const getAnswerCreatedAt = (answer: QuizAnswer): string | undefined => {
  const value = getValue(answer, ['createdAt', 'created_at']);
  return value ? String(value) : undefined;
};

const toIntegerId = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);

  return Number.isInteger(parsedValue) ? parsedValue : undefined;
};

const getQuizId = (quiz: QuizItem): string | undefined => {
  const value = getValue(quiz, ['id', 'quizId']);
  return value !== undefined && value !== null ? String(value) : undefined;
};

const getQuizLabel = (quiz: QuizItem): string => {
  return String(getValue(quiz, ['name', 'title', 'label', 'id']) || '-');
};

const getBlacklistEntryId = (entry: BlacklistEntry, index: number): string => {
  return String(
    getValue(entry, ['id', 'userQuizId']) ||
      `${getBlacklistEntryIdentifier(entry)}-${index}`
  );
};

const getBlacklistEntryUserId = (entry: BlacklistEntry): string | undefined => {
  return getFraudEntryActionId(entry);
};

const getBlacklistEntryIdentifier = (entry: BlacklistEntry): string => {
  return String(
    getValue(entry, [
      'identifier',
      'uid',
      'email',
      'user_email',
      'account_identifier',
    ]) || '-'
  );
};

const getBlacklistEntryEmail = (entry: BlacklistEntry): string => {
  return String(getValue(entry, ['email', 'identifier', 'uid']) || '-');
};

const getBlacklistEntryStatus = (entry: BlacklistEntry): string => {
  return String(getValue(entry, ['status']) || '-');
};

const getBlacklistEntryReason = (entry: BlacklistEntry): string => {
  return String(getValue(entry, ['reason', 'description', 'note']) || '-');
};

const getBlacklistEntryCreatedAt = (
  entry: BlacklistEntry
): string | undefined => {
  const value = getValue(entry, [
    'updatedAt',
    'createdAt',
    'created_at',
    'blacklisted_at',
    'updated_at',
  ]);
  return value ? String(value) : undefined;
};

const formatDate = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('sr-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDateWithMilliseconds = (value?: string): string => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

  return `${day}.${month}.${year}. ${hours}:${minutes}:${seconds}.${milliseconds}`;
};

const formatDuration = (value?: number): string => {
  if (value === undefined) {
    return '-';
  }

  if (value < 1000) {
    return `${value} ms`;
  }

  if (value < 60000) {
    const seconds = value / 1000;
    return `${seconds.toFixed(Number.isInteger(seconds) ? 0 : 1)} s`;
  }

  const minutes = Math.floor(value / 60000);
  const seconds = (value % 60000) / 1000;

  return `${minutes}m ${seconds.toFixed(Number.isInteger(seconds) ? 0 : 1)}s`;
};

const filterBlacklistEntries = (entries: BlacklistEntry[], search: string) => {
  return entries.filter((entry) => {
    const searchValue = search.toLowerCase();

    if (!searchValue) {
      return true;
    }

    return `${getBlacklistEntryIdentifier(entry)} ${getBlacklistEntryEmail(
      entry
    )} ${getBlacklistEntryReason(entry)} ${getBlacklistEntryStatus(entry)}`
      .toLowerCase()
      .includes(searchValue);
  });
};

const filterFlaggedUsers = (
  users: FlaggedUser[],
  search: string,
  statusFilter: FlaggedStatusFilter
) => {
  const searchValue = search.toLowerCase();

  return users.filter((user) => {
    const status = getFlaggedUserStatus(user);

    if (statusFilter !== 'all' && status !== statusFilter) {
      return false;
    }

    const identifier = getFlaggedUserIdentifier(user);
    const email = getFlaggedUserEmail(user);
    const reason = getFlaggedUserReason(user);

    if (!searchValue) {
      return true;
    }

    return `${identifier} ${email} ${reason} ${status}`
      .toLowerCase()
      .includes(searchValue);
  });
};

export const QuizBlacklistSection = observer((): ReactElement | null => {
  const { t } = useTranslation();
  const {
    currentPage,
    user: { isAdministrator, isModerator },
  } = useMst();
  const [flaggedUsers, setFlaggedUsers] = useState<FlaggedUser[]>([]);
  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const [selectedQuizId, setSelectedQuizId] = useState('');
  const [blacklistEntries, setBlacklistEntries] = useState<BlacklistEntry[]>(
    []
  );
  const [isFlaggedLoading, setIsFlaggedLoading] = useState(false);
  const [isQuizzesLoading, setIsQuizzesLoading] = useState(false);
  const [isBlacklistLoading, setIsBlacklistLoading] = useState(false);
  const [submittingUserKey, setSubmittingUserKey] = useState<string | null>(
    null
  );
  const [unbanningEntryId, setUnbanningEntryId] = useState<string | null>(null);
  const [flaggedSearchInput, setFlaggedSearchInput] = useState('');
  const [debouncedFlaggedSearch, setDebouncedFlaggedSearch] = useState('');
  const [flaggedStatusFilter, setFlaggedStatusFilter] =
    useState<FlaggedStatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [blacklistPage, setBlacklistPage] = useState(1);
  const [detailsEntryKey, setDetailsEntryKey] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedFlaggedSearch(flaggedSearchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [flaggedSearchInput]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setBlacklistPage(1);
  }, [debouncedSearch, selectedQuizId]);

  useEffect(() => {
    setDetailsEntryKey(null);
  }, [selectedQuizId]);

  const loadQuizzes = useCallback(async () => {
    if (!isAdministrator && !isModerator) {
      return;
    }

    setIsQuizzesLoading(true);

    try {
      const response = await currentPage.root.api.admin.getQuizzes();
      const nextQuizzes: QuizItem[] = response?.data || [];

      setQuizzes(nextQuizzes);
      setSelectedQuizId((currentSelectedQuizId) => {
        if (
          currentSelectedQuizId &&
          nextQuizzes.some((quiz) => getQuizId(quiz) === currentSelectedQuizId)
        ) {
          return currentSelectedQuizId;
        }

        return getQuizId(nextQuizzes[0]) || '';
      });
    } catch (error) {
      setQuizzes([]);
      setSelectedQuizId('');
    } finally {
      setIsQuizzesLoading(false);
    }
  }, [currentPage.root.api.admin, isAdministrator, isModerator]);

  const loadBlacklistEntries = useCallback(async () => {
    if ((!isAdministrator && !isModerator) || !selectedQuizId) {
      setFlaggedUsers([]);
      setBlacklistEntries([]);
      return;
    }

    setIsFlaggedLoading(true);
    setIsBlacklistLoading(true);

    try {
      const response = await currentPage.root.api.admin.getQuizFraudList(
        selectedQuizId
      );

      const entries: BlacklistEntry[] = response?.data || [];

      setFlaggedUsers(
        entries.filter((entry: BlacklistEntry) =>
          ['potential_fraud', 'not_fraud'].includes(
            String(getValue(entry, ['status']) || '')
          )
        )
      );
      setBlacklistEntries(
        entries.filter(
          (entry: BlacklistEntry) => getValue(entry, ['status']) === 'fraud'
        )
      );
    } catch (error) {
      setFlaggedUsers([]);
      setBlacklistEntries([]);
    } finally {
      setIsFlaggedLoading(false);
      setIsBlacklistLoading(false);
    }
  }, [
    currentPage.root.api.admin,
    isAdministrator,
    isModerator,
    selectedQuizId,
  ]);

  useEffect(() => {
    loadQuizzes();
  }, [loadQuizzes]);

  useEffect(() => {
    loadBlacklistEntries();
  }, [loadBlacklistEntries]);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadQuizzes(),
      selectedQuizId ? loadBlacklistEntries() : Promise.resolve(),
    ]);
  }, [loadBlacklistEntries, loadQuizzes, selectedQuizId]);

  const filteredFlaggedUsers = useMemo(
    () =>
      filterFlaggedUsers(
        flaggedUsers,
        debouncedFlaggedSearch,
        flaggedStatusFilter
      ),
    [debouncedFlaggedSearch, flaggedStatusFilter, flaggedUsers]
  );

  const filteredBlacklistEntries = useMemo(
    () => filterBlacklistEntries(blacklistEntries, debouncedSearch),
    [blacklistEntries, debouncedSearch]
  );

  const blacklistTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(filteredBlacklistEntries.length / BLACKLIST_PAGE_SIZE) || 1
      ),
    [filteredBlacklistEntries.length]
  );

  const paginatedBlacklistEntries = useMemo(
    () =>
      filteredBlacklistEntries.slice(
        (blacklistPage - 1) * BLACKLIST_PAGE_SIZE,
        blacklistPage * BLACKLIST_PAGE_SIZE
      ),
    [blacklistPage, filteredBlacklistEntries]
  );

  useEffect(() => {
    if (blacklistPage > blacklistTotalPages) {
      setBlacklistPage(blacklistTotalPages);
    }
  }, [blacklistPage, blacklistTotalPages]);

  const toggleDetails = useCallback((entryKey: string) => {
    setDetailsEntryKey((currentEntryKey) =>
      currentEntryKey === entryKey ? null : entryKey
    );
  }, []);

  if (!isAdministrator && !isModerator) {
    return null;
  }

  return (
    <div className={styles.SectionContainer}>
      <div className={styles.SectionHeader}>
        <div>
          <h3>{t('administration:quiz_blacklist_title')}</h3>
          <p>{t('administration:quiz_blacklist_description')}</p>
        </div>
        <button className={styles.SecondaryButton} onClick={refreshAll}>
          {t('administration:refresh')}
        </button>
      </div>

      <div className={styles.Grid}>
        <section className={styles.Card}>
          <div className={styles.CardHeader}>
            <div>
              <h4>{t('administration:flagged_users')}</h4>
              <span>{t('administration:flagged_users_hint')}</span>
            </div>
            {(isFlaggedLoading || isQuizzesLoading) && (
              <span>{t('administration:loading')}</span>
            )}
          </div>

          <div className={styles.FiltersRow}>
            <select
              className={styles.Select}
              value={selectedQuizId}
              onChange={(event) => setSelectedQuizId(event.target.value)}
              disabled={!quizzes.length}
            >
              {!quizzes.length ? (
                <option value="">{t('administration:no_quizzes')}</option>
              ) : (
                quizzes.map((quiz) => {
                  const quizId = getQuizId(quiz) || '';

                  return (
                    <option key={quizId} value={quizId}>
                      {getQuizLabel(quiz)}
                    </option>
                  );
                })
              )}
            </select>

            <select
              className={styles.Select}
              value={flaggedStatusFilter}
              onChange={(event) =>
                setFlaggedStatusFilter(
                  event.target.value as FlaggedStatusFilter
                )
              }
              disabled={!selectedQuizId}
            >
              {FLAGGED_STATUS_FILTERS.map((statusFilter) => (
                <option key={statusFilter} value={statusFilter}>
                  {t(`administration:flagged_status_filter_${statusFilter}`)}
                </option>
              ))}
            </select>

            <div className={styles.SearchFieldWrap}>
              <input
                className={styles.SearchInput}
                value={flaggedSearchInput}
                onChange={(event) => setFlaggedSearchInput(event.target.value)}
                placeholder={t('administration:search_flagged_placeholder')}
                disabled={!selectedQuizId}
              />
              {!!flaggedSearchInput && (
                <button
                  className={styles.ClearButton}
                  onClick={() => setFlaggedSearchInput('')}
                  type="button"
                >
                  {t('basic:cancel')}
                </button>
              )}
            </div>
          </div>

          <div className={styles.TableWrapper}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>{t('administration:identifier')}</th>
                  <th>{t('administration:email')}</th>
                  <th>{t('administration:status')}</th>
                  <th>{t('administration:quiz_completed_at')}</th>
                  <th>{t('administration:reason')}</th>
                  <th>{t('administration:actions')}</th>
                </tr>
              </thead>
              <tbody>
                {!filteredFlaggedUsers.length && !isFlaggedLoading && (
                  <tr>
                    <td colSpan={6} className={styles.EmptyState}>
                      {t('administration:no_flagged_users')}
                    </td>
                  </tr>
                )}

                {filteredFlaggedUsers.map((user, index) => {
                  const userKey = getFlaggedUserKey(user, index);
                  const userId = getFraudEntryActionId(user);
                  const actionId = toIntegerId(userId);
                  const userStatus = getFlaggedUserStatus(user);
                  const answers = getFraudEntryAnswers(user);
                  const isDetailsOpen =
                    detailsEntryKey === `flagged-${userKey}`;

                  return (
                    <React.Fragment key={userKey}>
                      <tr>
                        <td>{getFlaggedUserIdentifier(user)}</td>
                        <td>{getFlaggedUserEmail(user)}</td>
                        <td>
                          {t(`administration:fraud_status_${userStatus}`)}
                        </td>
                        <td>{formatDate(getFlaggedUserCompletedAt(user))}</td>
                        <td>{getFlaggedUserReason(user)}</td>
                        <td>
                          <div className={styles.ActionButtons}>
                            {userStatus === 'potential_fraud' && (
                              <button
                                className={styles.SecondaryButton}
                                disabled={
                                  submittingUserKey === userKey || !actionId
                                }
                                onClick={async () => {
                                  if (!actionId) {
                                    currentPage.root.showFailure(
                                      t('administration:missing_user_id'),
                                      ''
                                    );
                                    return;
                                  }

                                  setSubmittingUserKey(userKey);

                                  try {
                                    await currentPage.root.api.admin.setQuizNotFraud(
                                      {
                                        id: actionId,
                                      }
                                    );
                                    currentPage.root.showSuccess(
                                      t('administration:not_fraud_success'),
                                      ''
                                    );
                                    await refreshAll();
                                  } finally {
                                    setSubmittingUserKey(null);
                                  }
                                }}
                              >
                                {t('administration:mark_as_not_fraud')}
                              </button>
                            )}

                            <button
                              className={styles.PrimaryButton}
                              disabled={
                                submittingUserKey === userKey || !actionId
                              }
                              onClick={async () => {
                                if (!actionId) {
                                  currentPage.root.showFailure(
                                    t('administration:missing_user_id'),
                                    ''
                                  );
                                  return;
                                }

                                setSubmittingUserKey(userKey);

                                try {
                                  await currentPage.root.api.admin.setQuizFraud(
                                    {
                                      id: actionId,
                                    }
                                  );
                                  currentPage.root.showSuccess(
                                    t('administration:blacklist_success'),
                                    ''
                                  );
                                  await refreshAll();
                                } finally {
                                  setSubmittingUserKey(null);
                                }
                              }}
                            >
                              {t('administration:add_to_blacklist')}
                            </button>

                            <button
                              className={styles.SecondaryButton}
                              onClick={() =>
                                toggleDetails(`flagged-${userKey}`)
                              }
                              type="button"
                            >
                              {t(
                                isDetailsOpen
                                  ? 'administration:hide_answer_details'
                                  : 'administration:show_answer_details'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isDetailsOpen && (
                        <tr className={styles.DetailsRow}>
                          <td colSpan={6}>
                            <div className={styles.DetailsCard}>
                              <div className={styles.DetailsTitle}>
                                {t('administration:answers_details')}
                              </div>

                              {answers.length ? (
                                <div className={styles.TableWrapper}>
                                  <table className={styles.AnswersTable}>
                                    <thead>
                                      <tr>
                                        <th>{t('administration:question')}</th>
                                        <th>{t('administration:answer')}</th>
                                        <th>
                                          {t('administration:answer_duration')}
                                        </th>
                                        <th>
                                          {t('administration:answered_at')}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {answers.map((answer, answerIndex) => {
                                        const answerKey = String(
                                          getValue(answer, ['id']) ||
                                            `${userKey}-${answerIndex}`
                                        );

                                        return (
                                          <tr key={answerKey}>
                                            <td>
                                              {getAnswerQuestionText(answer)}
                                            </td>
                                            <td>{getAnswerText(answer)}</td>
                                            <td>
                                              {formatDuration(
                                                getAnswerDuration(answer)
                                              )}
                                            </td>
                                            <td>
                                              {formatDateWithMilliseconds(
                                                getAnswerCreatedAt(answer)
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className={styles.EmptyDetailsState}>
                                  {t('administration:no_answers_available')}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className={styles.Card}>
          <div className={styles.CardHeader}>
            <div>
              <h4>{t('administration:blacklist')}</h4>
              <span>{t('administration:blacklist_hint')}</span>
            </div>
            {(isBlacklistLoading || isQuizzesLoading) && (
              <span>{t('administration:loading')}</span>
            )}
          </div>

          <div className={styles.FiltersRow}>
            <div className={styles.SearchFieldWrap}>
              <input
                className={styles.SearchInput}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={t('administration:search_blacklist_placeholder')}
                disabled={!selectedQuizId}
              />
              {!!searchInput && (
                <button
                  className={styles.ClearButton}
                  onClick={() => setSearchInput('')}
                  type="button"
                >
                  {t('basic:cancel')}
                </button>
              )}
            </div>
          </div>

          <div className={styles.TableWrapper}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>{t('administration:identifier')}</th>
                  <th>{t('administration:email')}</th>
                  <th>{t('administration:status')}</th>
                  <th>{t('administration:reason')}</th>
                  <th>{t('administration:blacklisted_at')}</th>
                  <th>{t('administration:actions')}</th>
                </tr>
              </thead>
              <tbody>
                {!paginatedBlacklistEntries.length && !isBlacklistLoading && (
                  <tr>
                    <td colSpan={6} className={styles.EmptyState}>
                      {selectedQuizId
                        ? t('administration:no_blacklist_entries')
                        : t('administration:select_quiz')}
                    </td>
                  </tr>
                )}

                {paginatedBlacklistEntries.map((entry, index) => {
                  const entryId = getBlacklistEntryId(entry, index);
                  const userId = getBlacklistEntryUserId(entry);
                  const actionId = toIntegerId(userId);
                  const answers = getFraudEntryAnswers(entry);
                  const isDetailsOpen =
                    detailsEntryKey === `blacklist-${entryId}`;

                  return (
                    <React.Fragment key={entryId}>
                      <tr>
                        <td>{getBlacklistEntryIdentifier(entry)}</td>
                        <td>{getBlacklistEntryEmail(entry)}</td>
                        <td>{getBlacklistEntryStatus(entry)}</td>
                        <td>{getBlacklistEntryReason(entry)}</td>
                        <td>{formatDate(getBlacklistEntryCreatedAt(entry))}</td>
                        <td>
                          <div className={styles.ActionButtons}>
                            <button
                              className={styles.SecondaryButton}
                              disabled={
                                unbanningEntryId === entryId || !actionId
                              }
                              onClick={async () => {
                                if (!actionId) {
                                  currentPage.root.showFailure(
                                    t('administration:missing_user_id'),
                                    ''
                                  );
                                  return;
                                }

                                setUnbanningEntryId(entryId);

                                try {
                                  await currentPage.root.api.admin.setQuizNotFraud(
                                    {
                                      id: actionId,
                                    }
                                  );
                                  currentPage.root.showSuccess(
                                    t('administration:unblacklist_success'),
                                    ''
                                  );
                                  await loadBlacklistEntries();
                                } finally {
                                  setUnbanningEntryId(null);
                                }
                              }}
                            >
                              {t('administration:remove_from_blacklist')}
                            </button>

                            <button
                              className={styles.SecondaryButton}
                              onClick={() =>
                                toggleDetails(`blacklist-${entryId}`)
                              }
                              type="button"
                            >
                              {t(
                                isDetailsOpen
                                  ? 'administration:hide_answer_details'
                                  : 'administration:show_answer_details'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isDetailsOpen && (
                        <tr className={styles.DetailsRow}>
                          <td colSpan={6}>
                            <div className={styles.DetailsCard}>
                              <div className={styles.DetailsTitle}>
                                {t('administration:answers_details')}
                              </div>

                              {answers.length ? (
                                <div className={styles.TableWrapper}>
                                  <table className={styles.AnswersTable}>
                                    <thead>
                                      <tr>
                                        <th>{t('administration:question')}</th>
                                        <th>{t('administration:answer')}</th>
                                        <th>
                                          {t('administration:answer_duration')}
                                        </th>
                                        <th>
                                          {t('administration:answered_at')}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {answers.map((answer, answerIndex) => {
                                        const answerKey = String(
                                          getValue(answer, ['id']) ||
                                            `${entryId}-${answerIndex}`
                                        );

                                        return (
                                          <tr key={answerKey}>
                                            <td>
                                              {getAnswerQuestionText(answer)}
                                            </td>
                                            <td>{getAnswerText(answer)}</td>
                                            <td>
                                              {formatDuration(
                                                getAnswerDuration(answer)
                                              )}
                                            </td>
                                            <td>
                                              {formatDateWithMilliseconds(
                                                getAnswerCreatedAt(answer)
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <div className={styles.EmptyDetailsState}>
                                  {t('administration:no_answers_available')}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={styles.PaginationRow}>
            <span>
              {t('administration:page')} {blacklistPage} / {blacklistTotalPages}
            </span>
            <div className={styles.PaginationActions}>
              <button
                className={styles.SecondaryButton}
                disabled={blacklistPage <= 1}
                onClick={() =>
                  setBlacklistPage((page) => Math.max(1, page - 1))
                }
              >
                {t('administration:previous')}
              </button>
              <button
                className={styles.SecondaryButton}
                disabled={blacklistPage >= blacklistTotalPages}
                onClick={() =>
                  setBlacklistPage((page) =>
                    Math.min(blacklistTotalPages, page + 1)
                  )
                }
              >
                {t('administration:next')}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
});
